"use client";

import { ProductCard } from "@repo/ui/components/product-card";
import { Loading } from "@repo/ui/components/skeleton";
import { buttonVariants } from "@repo/ui/components/ui/button";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";

import { getProducts, type CatalogProduct, type ProductsPage } from "../../../../lib/api-client";
import { priceLabel } from "../../../../lib/product-display";
import { storeHref, type StoreState } from "../../../../lib/store-href";
import { useLivePrices, useSecondsAgo } from "../../../../lib/use-live-prices";
import { ProductGridSkeleton } from "../../_components/PageSkeletons";

type Status = "idle" | "loading" | "error";

// Starts from the server-rendered page (what crawlers and the first paint
// see), then appends the following pages from /api as the visitor nears the
// bottom. The "show more" link is a real <a href> to the next page: crawlers
// follow it, and it's the fallback if the observer never fires. Prices are
// patched in place as they move (useLivePrices).
export function StoreFeed({ initial, state, pageSize }: { initial: ProductsPage; state: StoreState; pageSize: number }) {
  const [items, setItems] = useState<CatalogProduct[]>(initial.products);
  const [page, setPage] = useState(initial.page);
  const [hasMore, setHasMore] = useState(initial.hasMore);
  const [status, setStatus] = useState<Status>("idle");
  const loadingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const live = useLivePrices({ updatedAt: initial.pricesUpdatedAt });
  const updatedAgo = useSecondsAgo(live.updatedAt);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setStatus("loading");
    const { metal, weight, fineness, brand, sort } = state;
    const { status: httpStatus, data } = await getProducts({ metal, weight, fineness, brand, sort, page: page + 1, pageSize });
    loadingRef.current = false;
    if (httpStatus !== 200 || !data) {
      setStatus("error");
      return;
    }
    // Pages can overlap when the catalog changes while someone scrolls;
    // drop repeats rather than render a card twice.
    setItems((prev) => {
      const seen = new Set(prev.map((item) => item.id));
      return [...prev, ...data.products.filter((item) => !seen.has(item.id))];
    });
    setPage(data.page);
    setHasMore(data.hasMore);
    setStatus("idle");
  }, [hasMore, page, pageSize, state]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || status === "error") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadMore();
      },
      { rootMargin: "800px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, status]);

  if (items.length === 0) {
    return <p className="text-muted-foreground py-10 text-center text-sm">محصولی با این مشخصات موجود نیست.</p>;
  }

  return (
    <div>
      <p className="text-muted-foreground mb-3 text-xs" aria-live="polite">
        {initial.total.toLocaleString("fa-IR")} محصول
        {updatedAgo ? ` · قیمت‌ها لحظه‌ای، به‌روزرسانی ${updatedAgo}` : ""}
      </p>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
        {items.map((item) => {
          const price = item.id in live.prices ? live.prices[item.id]! : item.price.priceRial;
          return (
            <Link key={item.id} href={`/store/${item.slug}`} className="block transition-opacity hover:opacity-90">
            <ProductCard
              title={item.title}
              subtitle={[item.metal === "gold" ? item.fineness.label : null, item.brand.name].filter(Boolean).join(" · ")}
              badge={item.weight.label}
              gift={item.gift}
              imageUrl={item.images[0]?.url}
              imageAlt={item.images[0]?.alt}
              priceLabel={priceLabel(price)}
              priceHalted={price === null}
              priceClassName={live.changed.has(item.id) ? "bg-tint-yellow" : undefined}
              stock={item.availableQuantity}
            />
            </Link>
          );
        })}
      </div>

      {/* While the next page loads, its cards' skeletons continue the grid. */}
      <div ref={sentinelRef} className={status === "loading" ? "pt-3 pb-8 sm:pt-4" : "flex justify-center py-8"}>
        {status === "error" ? (
          <button type="button" onClick={() => void loadMore()} className="text-foreground text-sm underline underline-offset-4">
            خطا در دریافت محصولات، تلاش دوباره
          </button>
        ) : status === "loading" ? (
          <Loading>
            <ProductGridSkeleton count={4} />
          </Loading>
        ) : hasMore ? (
          <Link
            href={storeHref({ ...state, page: page + 1 })}
            onClick={(event) => {
              event.preventDefault();
              void loadMore();
            }}
            className={buttonVariants({ variant: "outline" })}
          >
            نمایش بیشتر
          </Link>
        ) : null}
      </div>
    </div>
  );
}
